import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "alp_admin_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Retry wrapper for critical public reads so a transient failure never leaves the UI blank.
const withRetry = async (fn, tries = 4, delay = 700) => {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (i < tries - 1) await new Promise((r) => setTimeout(r, delay * (i + 1)));
    }
  }
  throw lastErr;
};

// Public
export const getConfig = () => withRetry(() => api.get("/config").then((r) => r.data));
export const getNoticeTypes = () => api.get("/notice-types").then((r) => r.data);
export const getCategories = () => withRetry(() => api.get("/categories").then((r) => r.data));
export const createNotice = (payload) => api.post("/notices", payload).then((r) => r.data);
export const patchNotice = (id, payload) => api.patch(`/notices/${id}`, payload).then((r) => r.data);
export const getNotice = (id) => api.get(`/notices/${id}`).then((r) => r.data);
export const generateNotice = (id, tone = "firm", custom_instructions = null) =>
  api.post(`/notices/${id}/generate`, { tone, custom_instructions }).then((r) => r.data);
export const updateText = (id, edited_text) =>
  api.put(`/notices/${id}/text`, { edited_text }).then((r) => r.data);
export const createOrder = (id) => api.post(`/notices/${id}/create-order`).then((r) => r.data);
export const getUpiQr = (id) => api.post(`/notices/${id}/upi-qr`).then((r) => r.data);
export const getPaymentStatus = (id) => api.get(`/notices/${id}/payment-status`).then((r) => r.data);
export const updateDelivery = (id, body) => api.put(`/notices/${id}/delivery`, body).then((r) => r.data);
export const verifyPayment = (id, body) =>
  api.post(`/notices/${id}/verify-payment`, body).then((r) => r.data);
export const submitReview = (id) => api.post(`/notices/${id}/submit-review`).then((r) => r.data);
export const uploadAttachment = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/notices/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteAttachment = (id, attId) => api.delete(`/notices/${id}/attachments/${attId}`).then((r) => r.data);
export const attachmentUrl = (id, attId) => `${API}/notices/${id}/attachments/${attId}`;
export const trackNotice = (ref) => api.get("/track", { params: { ref } }).then((r) => r.data);
export const logVisit = (visitor_id, path) =>
  api.post("/events/visit", { visitor_id, path }).then((r) => r.data).catch(() => {});
export const getAnalytics = (period) =>
  api.get("/admin/analytics", { params: { period } }).then((r) => r.data);
export const pdfUrl = (id) => `${API}/notices/${id}/pdf`;
export const adminPdfUrl = (id) => `${API}/notices/${id}/pdf?t=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;

// Auth / Admin
export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const adminListNotices = (status) =>
  api.get("/admin/notices", { params: status ? { status } : {} }).then((r) => r.data);
export const adminGetNotice = (id) => api.get(`/admin/notices/${id}`).then((r) => r.data);
export const adminUpdateText = (id, edited_text) =>
  api.put(`/admin/notices/${id}/text`, { edited_text }).then((r) => r.data);
export const adminApprove = (id) => api.post(`/admin/notices/${id}/approve`).then((r) => r.data);
export const adminReject = (id, note) => api.post(`/admin/notices/${id}/reject`, { note }).then((r) => r.data);
export const adminDecline = (id, note) => api.post(`/admin/notices/${id}/decline`, { note }).then((r) => r.data);
export const adminSend = (id) => api.post(`/admin/notices/${id}/send`).then((r) => r.data);
export const adminDeleteNotice = (id) => api.delete(`/admin/notices/${id}`).then((r) => r.data);
export const adminFulfil = (id, body) => api.put(`/admin/notices/${id}/fulfilment`, body).then((r) => r.data);
